import DOMPurify from 'isomorphic-dompurify'

export type InlineFormat = 'bold' | 'italic' | 'strike' | 'code' | 'underline'

const FORMAT_MARKERS: Record<InlineFormat, [string, string]> = {
  bold: ['**', '**'],
  italic: ['_', '_'],
  underline: ['__', '__'],
  strike: ['~~', '~~'],
  code: ['`', '`'],
}

const URL_RE = /https?:\/\/[^\s<>"']+/gi
const URL_MATCH_RE = /https?:\/\/[^\s<>"']+/i

/** Убирает пунктуацию и обрамляющие скобки, случайно попавшие в URL. */
export function trimDetectedUrl(rawUrl: string, charBefore = ''): string {
  let url = rawUrl.replace(/[.,;:!?]+$/, '')

  const countParens = (value: string) => {
    let opens = 0
    let closes = 0
    for (const ch of value) {
      if (ch === '(') {
        opens += 1
      } else if (ch === ')') {
        closes += 1
      }
    }
    return { opens, closes }
  }

  if (charBefore === '(' && url.endsWith(')')) {
    const { opens, closes } = countParens(url)
    if (closes > opens) {
      url = url.slice(0, -1)
    }
  } else {
    while (url.endsWith(')')) {
      const { opens, closes } = countParens(url)
      if (closes > opens) {
        url = url.slice(0, -1)
      } else {
        break
      }
    }
  }

  if (charBefore === '[' && url.endsWith(']')) {
    url = url.slice(0, -1)
  }

  return url
}

/** Оборачивает выделенный фрагмент markdown-маркерами. */
export function wrapInlineFormat(
  text: string,
  start: number,
  end: number,
  format: InlineFormat,
): { text: string; cursor: number; selectStart: number; selectEnd: number } {
  const [open, close] = FORMAT_MARKERS[format]
  const from = Math.max(0, Math.min(start, end, text.length))
  const to = Math.max(from, Math.max(start, end))
  const selected = text.slice(from, to)

  if (!selected) {
    const next = `${text.slice(0, from)}${open}${close}${text.slice(to)}`
    const cursor = from + open.length
    return {
      text: next,
      cursor,
      selectStart: cursor,
      selectEnd: cursor,
    }
  }

  const next = `${text.slice(0, from)}${open}${selected}${close}${text.slice(to)}`
  const selectStart = from + open.length
  const selectEnd = selectStart + selected.length
  return {
    text: next,
    cursor: selectEnd + close.length,
    selectStart,
    selectEnd,
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** Markdown-лайт (** _ __ ~~ `) + автоссылки → безопасный HTML. */
export function markdownToSafeHtml(raw: string): string {
  if (!raw) {
    return ''
  }

  let html = escapeHtml(raw)
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>')
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  html = html.replace(/__([^_]+)__/g, '<u>$1</u>')
  html = html.replace(/(^|[^_*])_([^_]+)_(?![*_])/g, '$1<em>$2</em>')
  html = html.replace(/~~([^~]+)~~/g, '<del>$1</del>')
  html = html.replace(URL_RE, (url, offset, source) => {
    const charBefore = offset > 0 ? source[offset - 1] : ''
    const cleaned = trimDetectedUrl(url, charBefore)
    const safe = escapeHtml(cleaned)
    const trailing = escapeHtml(url.slice(cleaned.length))
    return `<a href="${safe}" target="_blank" rel="noopener noreferrer">${safe}</a>${trailing}`
  })
  html = html.replace(/\n/g, '<br>')

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['strong', 'em', 'u', 'del', 'code', 'a', 'br'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  })
}

/** HTML для Matrix `formatted_body`. */
export function markdownToMatrixFormattedBody(raw: string): string {
  return markdownToSafeHtml(raw)
}

export function extractFirstUrl(text: string): string | null {
  const match = URL_MATCH_RE.exec(text)
  if (!match) {
    return null
  }
  const charBefore = match.index > 0 ? text[match.index - 1] : ''
  return trimDetectedUrl(match[0], charBefore)
}

export function hasMarkdownMarkup(text: string): boolean {
  return /(\*\*[^*]+\*\*|__[^_]+__|(^|[^_*])_[^_]+_(?![*_])|~~[^~]+~~|`[^`]+`)/.test(text)
}

/** Убирает markdown-маркеры — для превью в списке чатов. */
export function markdownToPlainText(raw: string): string {
  if (!raw) {
    return ''
  }

  return raw
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/(^|[^_*])_([^_]+)_(?![*_])/g, '$1$2')
    .replace(/~~([^~]+)~~/g, '$1')
}
